<template>
  <section v-if="isVisible && !isCatalogEmpty" :class="$style.section">
    <aw-spring :desktop-size="12">
      <aw-catalog
        :main-title-override="sectionTitle"
        :hide-all-button="true"
        :breakpoints-overwrite="breakpointsOverwrite"
        :analytics-banner-interaction="analyticsBannerInteraction"
        analytics-measurable
        @is-empty="isCatalogEmpty = $event"
      />
    </aw-spring>
  </section>
</template>

<script setup>
  import { computed, defineAsyncComponent, provide, ref } from 'vue';
  import { useNuxtApp, useRoute } from 'nuxt/app';
  import AwSpring from '~~/common/components/AwSpring';
  import { useCatalogFooterStore } from '~~/shop/app/stores/catalogFooter';
  import { useUserInterfaceStore } from 'common/stores/userInterface.js';
  import { AnalyticsBannerInteraction } from 'common/utils/AnalyticsBannerInteraction.ts';
  import { calculatePromotionCompanyNo, calculatePromotionPageHref } from 'common/utils/analyticsHelpers.ts';

  const AwCatalog = defineAsyncComponent(() => import('~~/common/components/AwCatalog'));

  provide('catalogFooterVariant', true);

  const {
    $getHref,
    $getHost,
  } = useNuxtApp();

  const route = useRoute();
  const userInterfaceStore = useUserInterfaceStore();
  const catalogFooterStore = useCatalogFooterStore();

  const screenRange = computed(() => userInterfaceStore.mediaQueries);

  const isVisible = computed(() => catalogFooterStore.isCatalogFooterSectionVisible(route));
  const isCatalogEmpty = ref(false);
  const sectionTitle = computed(() => catalogFooterStore.getSectionTitle ?? '');

  const breakpointsOverwrite = computed(() => {
    return [
      {
        at: screenRange.value['desktop-large-min'],
        maxCount: 5,
        count: 'fit',
      },
      {
        at: screenRange.value['desktop-medium-min'],
        count: 'fit',
      },
      {
        at: screenRange.value['desktop-small-min'],
        count: 'fit',
      },
      {
        at: screenRange.value['tablet-min'],
        count: 'fit',
      },
      {
        at: true,
        count: 'auto',
      },
    ];
  });

  const analyticsBannerInteraction = AnalyticsBannerInteraction.create({
    promotionComponentId: 'footer_catalogs',
    promotionIndex: 1,
    promotionBannerIndex: 1,
    promotionCompanyNo: calculatePromotionCompanyNo(),
    promotionPageHref: calculatePromotionPageHref($getHref(), $getHost()),
    promotionName: null,
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.section {
  margin-bottom: 20px;
  padding: 20px 0;
  border-radius: 24px;
  background: $color-white;

  @include tablet(min) {
    --awTabCarouselJustifyContent: center;
    --awTabCarouselColumnTemplate: 260px;

    margin-bottom: 40px;
    padding: 40px 0;
    border-radius: 32px;
  }
}
</style>
